import { useState, useEffect } from 'react'
import {
  Title, Card, TextInput, NumberInput, Switch, Button, Group, Stack, Text,
  Divider, Loader, SimpleGrid,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { api } from '@/lib/api'

interface Plan {
  id: string
  name: string
  price: number
  duration: number
  description: string
  savings?: string
  isActive: boolean
}

interface PaymentMethod {
  id: string
  name: string
  description: string
  isActive: boolean
}

export default function PaymentSettings() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const [plans, setPlans] = useState<Plan[]>([])
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])

  useEffect(() => {
    api.admin.getPaymentSettings()
      .then((res: any) => {
        setPlans(res.plans || [])
        setPaymentMethods(res.paymentMethods || [])
      })
      .catch(() => {
        notifications.show({ title: 'Алдаа', message: 'Тохиргоо ачаалахад алдаа гарлаа', color: 'red' })
      })
      .finally(() => setLoading(false))
  }, [])

  const updatePlan = (index: number, field: keyof Plan, value: unknown) => {
    setPlans(prev => prev.map((p, i) => i === index ? { ...p, [field]: value } : p))
  }

  const updateMethod = (index: number, field: keyof PaymentMethod, value: unknown) => {
    setPaymentMethods(prev => prev.map((m, i) => i === index ? { ...m, [field]: value } : m))
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await api.admin.updatePaymentSettings({
        plans,
        paymentMethods,
      })
      notifications.show({ title: 'Амжилттай', message: 'Тохиргоо хадгалагдлаа', color: 'green' })
    } catch {
      notifications.show({ title: 'Алдаа', message: 'Хадгалахад алдаа гарлаа', color: 'red' })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <Loader />
      </div>
    )
  }

  return (
    <div>
      <Group justify="space-between" mb="lg">
        <Title order={2}>Төлбөрийн тохиргоо</Title>
        <Button onClick={handleSave} loading={saving}>Хадгалах</Button>
      </Group>

      {/* Premium Plans */}
      <Title order={4} mb="sm">Премиум багцууд</Title>
      <Stack gap="md" mb="xl">
        {plans.map((plan, i) => (
          <Card key={plan.id} shadow="xs" padding="md" radius="md" withBorder>
            <Group justify="space-between" mb="sm">
              <Text fw={600}>{plan.id === 'monthly' ? 'Сарын' : plan.id === 'quarterly' ? 'Улирлын' : 'Жилийн'} багц</Text>
              <Switch
                label="Идэвхтэй"
                checked={plan.isActive}
                onChange={(e) => updatePlan(i, 'isActive', e.currentTarget.checked)}
              />
            </Group>
            <SimpleGrid cols={{ base: 1, sm: 3 }}>
              <TextInput
                label="Нэр"
                value={plan.name}
                onChange={(e) => updatePlan(i, 'name', e.currentTarget.value)}
              />
              <NumberInput
                label="Үнэ (₮)"
                value={plan.price}
                onChange={(v) => updatePlan(i, 'price', v || 0)}
                min={0}
                thousandSeparator=","
              />
              <NumberInput
                label="Хугацаа (хоног)"
                value={plan.duration}
                onChange={(v) => updatePlan(i, 'duration', v || 0)}
                min={1}
              />
            </SimpleGrid>
            <SimpleGrid cols={{ base: 1, sm: 2 }} mt="sm">
              <TextInput
                label="Тайлбар"
                value={plan.description}
                onChange={(e) => updatePlan(i, 'description', e.currentTarget.value)}
              />
              <TextInput
                label="Хэмнэлт"
                value={plan.savings || ''}
                onChange={(e) => updatePlan(i, 'savings', e.currentTarget.value)}
                placeholder="жишээ: 11%"
              />
            </SimpleGrid>
          </Card>
        ))}
      </Stack>

      <Divider mb="xl" />

      {/* Payment Methods */}
      <Title order={4} mb="sm">Төлбөрийн аргууд</Title>
      <Stack gap="md" mb="xl">
        {paymentMethods.map((method, i) => (
          <Card key={method.id} shadow="xs" padding="md" radius="md" withBorder>
            <Group justify="space-between" mb="sm">
              <Text fw={600}>{method.name}</Text>
              <Switch
                label="Идэвхтэй"
                checked={method.isActive}
                onChange={(e) => updateMethod(i, 'isActive', e.currentTarget.checked)}
              />
            </Group>
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <TextInput
                label="Нэр"
                value={method.name}
                onChange={(e) => updateMethod(i, 'name', e.currentTarget.value)}
              />
              <TextInput
                label="Тайлбар"
                value={method.description}
                onChange={(e) => updateMethod(i, 'description', e.currentTarget.value)}
              />
            </SimpleGrid>
          </Card>
        ))}
      </Stack>


      <Group justify="flex-end">
        <Button onClick={handleSave} loading={saving} size="md">Бүгдийг хадгалах</Button>
      </Group>
    </div>
  )
}
